// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React from "react";
import { Box, Button } from "@material-ui/core";
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import ClearIcon from '@material-ui/icons/Clear';
import { useIntl } from "react-intl";
import clsx from "clsx";
import PropTypes from 'prop-types';
import { FormTextField } from "../../../../components";
import { getI18n } from "../../../../utils/intl";

export const OrderableListOption = ({
  ordinal, index, moveCard, value, classes, handleOptions, handleDeleteOption,
  handleAddOptionsOnKeyPress, showClearIcon,
}) => {

  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: "card",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    item: {
      type: "card",
      ordinal: ordinal,
      index: index,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  const intl = getI18n(useIntl());
  const optionsContainer = clsx(classes.optionsContainer, classes.flexStart);

  return (
    <Box className={optionsContainer} ref={ref} style={{ opacity }}
      data-handler-id={handlerId}>
      <Button variant="text" className={classes.dragIconButton}
        id={`custom-fields-option-drag-button-${ordinal}`}
        data-apim-test={`custom-fields-option-drag-button-${ordinal}`}
      >
        <DragIndicatorIcon className={classes.dragIcon} />
      </Button>
      <FormTextField
        placeholder={intl.getI18nMessage("label.custom.fields.add.option.placeholder")}
        handleChange={(value) => handleOptions(ordinal, value)}
        value={value}
        fieldContainerClass={classes.optionFieldContainer}
        onKeyPress={handleAddOptionsOnKeyPress}
        autoFocus={true}
        id={`custom-fields-option-${ordinal}`}
        maxLength='255'
      />
      {showClearIcon && <Button variant="outlined" className={classes.crossIcon}
        onClick={() => handleDeleteOption(ordinal)}
        id={`custom-fields-option-clear-button-${ordinal}`}
        data-apim-test={`custom-fields-option-clear-button-${ordinal}`}
      >{<ClearIcon />}</Button>
      }
    </Box>
  );
};

OrderableListOption.propTypes = {
  ordinal: PropTypes.number,
  index: PropTypes.number,
  moveCard: PropTypes.func,
  value: PropTypes.string,
  classes: PropTypes.object,
  handleOptions: PropTypes.func,
  handleDeleteOption: PropTypes.func,
  handleAddOptionsOnKeyPress: PropTypes.func,
  showClearIcon: PropTypes.bool,
}

export default OrderableListOption;
